import React, { useState, useEffect } from 'react';
import { X, Star, Trash2 } from 'lucide-react';
import { ScrollArea } from '../ui/scroll-area';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { API, getUserPhone } from '../../lib/api';

export default function FavouritesPanel({ isOpen, onClose }) {
  const [favourites, setFavourites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setLoading(true);
      axios.get(`${API}/api/favourites`, { params: { phone: getUserPhone() } })
        .then(res => setFavourites(res.data))
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  }, [isOpen]);

  const handleDelete = async (id) => {
    await axios.delete(`${API}/api/favourites/${id}`);
    setFavourites(prev => prev.filter(f => f.id !== id));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden" data-testid="favourites-panel">
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center">
              <Star className="w-5 h-5 text-amber-600 fill-amber-600" />
            </div>
            <div>
              <h2 className="text-base font-bold text-slate-900">Saved Responses</h2>
              <p className="text-xs text-slate-400">{favourites.length} saved</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100 text-slate-400">
            <X className="w-5 h-5" />
          </button>
        </div>

        <ScrollArea className="flex-1 px-5 pb-5">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-pulse text-slate-400 text-sm">Loading...</div>
            </div>
          ) : favourites.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Star className="w-10 h-10 text-slate-200 mb-3" />
              <p className="text-sm text-slate-400">No saved responses yet</p>
              <p className="text-xs text-slate-300 mt-1">Star any AI response to save it here</p>
            </div>
          ) : (
            <div className="space-y-3 pt-3">
              {favourites.map(fav => (
                <div
                  key={fav.id}
                  className="rounded-xl border border-slate-100 bg-white hover:border-slate-200 transition-colors cursor-pointer"
                  data-testid={`fav-item-${fav.id}`}
                >
                  <div
                    className="px-4 py-3 flex items-start justify-between gap-3"
                    onClick={() => setExpanded(expanded === fav.id ? null : fav.id)}
                  >
                    <div className="min-w-0 flex-1">
                      <span className="text-xs text-slate-400 bg-slate-50 px-2 py-0.5 rounded-full">{fav.mode}</span>
                      <p className="text-sm text-slate-700 mt-1.5 line-clamp-2">{fav.content.slice(0, 150)}...</p>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDelete(fav.id); }}
                      className="p-1.5 rounded hover:bg-red-50 text-slate-300 hover:text-red-500 transition-colors flex-shrink-0"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  {expanded === fav.id && (
                    <div className="px-4 pb-4 border-t border-slate-50 pt-3">
                      <div className="prose prose-sm prose-slate max-w-none text-xs">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{fav.content}</ReactMarkdown>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </div>
    </div>
  );
}
